import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { OperationRequest } from '@app/core/models/demand/demand-request';
import { DemandOutput } from '@app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-management/ndf-management.types';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { AppState } from '@app/state';
import { selectDateFormat } from '@app/state/auth/auth.selectors';
import { IdValue } from '@interfaces/a-organizar/idValue';
import {
  NdfDatePTAXExceeded,
  NdfDemandsCalculationsFiltered,
  NdfDemandsCalculationsOperation,
} from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { NdfOperationDates } from '@interfaces/a-organizar/ndfOperationDates';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { TourBuilder, TourService, TourStep } from '@services/helpers/tour.service';
import { UtilsService } from '@services/helpers/utils.service';
import { LoanOperationHttpService } from '@services/http/loanOperation/loan-operation-http.service';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { DateFormatterService } from '@src/app/core/services/helpers/date-formatter.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { parseISO } from 'date-fns';
import { MenuItem } from 'primeng/api';
import { Subject, of } from 'rxjs';
import { debounceTime, delay, first, switchMap } from 'rxjs/operators';

interface InstallmentOption {
  label: string;
  value: string;
}
export interface OperationAction {
  action:
    | 'save-demand'
    | 'duplicate'
    | 'remove'
    | 'pin-unpin'
    | 'check-uncheck'
    | 'quote'
    | 'hire'
    | 'highlight'
    | 'block'
    | 'move-up'
    | 'move-down'
    | 'move-last'
    | 'revert-liquidation-to-open'
    | 'revert-quoted-to-simulated'
    | 'revert-hired-to-quoted'
    | 'attachments';
  item: NdfDemandsCalculationsOperation;
  index?: number;
  controlName?: string;
}

interface LoanInstallment {
  id: string;
  installmentNumber?: number;
  dueAt: string;
  isFlatFee?: boolean;
}

interface LoanOperation {
  flowParams: {
    installments: LoanInstallment[];
  };
}

@Component({
  selector: 'app-ndf-management-item',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  templateUrl: './ndf-management-item.component.html',
  styleUrls: ['./ndf-management-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NdfManagementItemComponent
  extends VaultBaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() index: number = 0;
  @Input() ndfOperation: NdfDemandsCalculationsOperation | null = null;
  @Input() demand: NdfDemandsCalculationsFiltered | null = null;
  @Input() financialInstitutionsOptions: Option[] = [];
  @Output() sendDemand: EventEmitter<DemandOutput | null> = new EventEmitter<DemandOutput | null>();
  @Output() sendOperationAction: EventEmitter<OperationAction> =
    new EventEmitter<OperationAction>();
  @Output() sendCalculateDueDate: EventEmitter<NdfOperationDates> =
    new EventEmitter<NdfOperationDates>();

  demandPermissions = Permissions.DerivativeCalculator;
  currentDate = new Date().toISOString().split('T')[0];
  private dueDateSubject = new Subject<NdfOperationDates>();
  public demandRegisterProps = nameof<OperationRequest>();
  minDueDate: Date | null = null;

  operationStrategyTypeOptions: Option[] = [
    { label: 'NDF Vanilla', value: 'NDFVanilla' },
    { label: 'NDF CAP', value: 'NDFCAP' },
    { label: 'Call Spread', value: 'Callspread' },
    { label: 'Call Vanilla', value: 'CallVanilla' },
    { label: 'SWAP', value: 'SWAP' },
  ];

  activeIndexerOptions: Option[] = [
    { label: 'USD', value: 'USDBRL' },
    { label: 'EUR', value: 'EURBRL' },
  ];

  passiveIndexerOptions: Option[] = [
    { label: 'PRE', value: 'Pre' },
    { label: 'CDI', value: 'CDI' },
  ];

  dayCountConventionOptions: Option[] = [
    { label: '360 DC', value: '360' },
    { label: '252 DU', value: '252' },
  ];

  interestFlowOptions: Option[] = [
    { label: 'Mensal', value: 'Monthly' },
    { label: 'Trimestral', value: 'Quarterly' },
    { label: 'Semestral', value: 'Biannually' },
    { label: 'Anual', value: 'Annually' },
  ];

  amortizationFlowOptions: Option[] = [
    { label: 'Mensal', value: 'Monthly' },
    { label: 'Trimestral', value: 'Quarterly' },
    { label: 'Semestral', value: 'Biannually' },
    { label: 'Anual', value: 'Annually' },
    { label: 'Bullet', value: 'Bullet' },
  ];

  demandForm = this.formBuilder.group({
    [this.demandRegisterProps.id]: [''],
    [this.demandRegisterProps.operationStrategyType]: ['', Validators.required],
    [this.demandRegisterProps.financialInstitution]: ['', Validators.required],
    [this.demandRegisterProps.operationDate]: [''],
    [this.demandRegisterProps.dueAt]: ['', Validators.required],
    [this.demandRegisterProps.daysCount]: [''],
    [this.demandRegisterProps.businessDaysCount]: [''],
    [this.demandRegisterProps.notionalAmount]: [''],
    [this.demandRegisterProps.forwardRate]: [''],
    [this.demandRegisterProps.capValue]: [''],
    [this.demandRegisterProps.referenceExchangeRate]: [''],
    [this.demandRegisterProps.reference]: [''],
    [this.demandRegisterProps.strikeBuy]: [''],
    [this.demandRegisterProps.strikeSell]: [''],
    [this.demandRegisterProps.premium]: [''],
    [this.demandRegisterProps.numberOfInstallments]: [''],
    [this.demandRegisterProps.activeIndexer]: [''],
    [this.demandRegisterProps.passiveIndexer]: [''],
    [this.demandRegisterProps.activeFixedRatePerYear]: [''],
    [this.demandRegisterProps.passiveFixedRatePerYear]: [''],
    [this.demandRegisterProps.interestFlow]: [''],
    [this.demandRegisterProps.amortizationFlow]: [''],
    [this.demandRegisterProps.numberOfAmortizations]: [''],
    [this.demandRegisterProps.dayCountConvention]: [''],
    [this.demandRegisterProps.estimetedCdiForPeriod]: [''],
  });

  get operationsIsValid(): boolean {
    return (
      this.demand?.operations?.every(
        operation => operation.financialInstitutionId && operation.dueAt
      ) || false
    );
  }

  get canEdit(): boolean {
    return (
      this.ndfOperation?.operationStatus !== 'Quoted' &&
      this.ndfOperation?.operationStatus !== 'Hired'
    );
  }

  get isCapReadonly(): boolean {
    return this.ndfOperation?.operationStrategyType !== 'NDFCAP';
  }

  get isStrikeBuyReadonly(): boolean {
    return !['CALLVANILLA', 'CALLSPREAD'].includes(
      this.ndfOperation?.operationStrategyType?.toUpperCase() || ''
    );
  }

  get isCallSpread(): boolean {
    return this.ndfOperation?.operationStrategyType === 'CallSpread';
  }

  get isCallVanilla(): boolean {
    return this.ndfOperation?.operationStrategyType === 'CallVanilla';
  }

  get isStrikeSellReadonly(): boolean {
    return (
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'Callspread'
    );
  }

  get isSwapFieldsReadonly(): boolean {
    return this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP';
  }
  get isPremiumReadonly(): boolean {
    return !['CALLVANILLA', 'CALLSPREAD', 'SWAP'].includes(
      this.ndfOperation?.operationStrategyType?.toUpperCase() || ''
    );
  }

  get isForwardRateReadonly(): boolean {
    return !['NDFVANILLA', 'NDFCAP'].includes(
      this.ndfOperation?.operationStrategyType?.toUpperCase() || ''
    );
  }

  // Para todos os campos do SWAP
  get isNumberOfInstallmentsReadonly(): boolean {
    return (
      !this.canEdit ||
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP'
    );
  }

  get isActiveIndexerReadonly(): boolean {
    return (
      !this.canEdit ||
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP'
    );
  }

  get isPassiveIndexerReadonly(): boolean {
    return (
      !this.canEdit ||
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP'
    );
  }

  get isActiveFixedRateReadonly(): boolean {
    return (
      !this.canEdit ||
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP'
    );
  }

  get isPassiveFixedRateReadonly(): boolean {
    return (
      !this.canEdit ||
      this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value !== 'SWAP'
    );
  }

  getInterestFlowLabel(value: string | undefined): string {
    if (!value) return '-';
    const option = this.interestFlowOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  getAmortizationFlowLabel(value: string | undefined): string {
    if (!value) return '-';
    const option = this.amortizationFlowOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  get selectedInterestFlow(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.interestFlow)?.value;
    return value ? (this.interestFlowOptions.find(opt => opt.value === value) ?? null) : null;
  }

  get selectedAmortizationFlow(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.amortizationFlow)?.value;
    return value ? (this.amortizationFlowOptions.find(opt => opt.value === value) ?? null) : null;
  }

  actionIcons: MenuItem[] = [];
  showApplyButton: boolean = false;
  lastDatePTAXExceededCap: string = '';
  dateFormat: string = 'dd/MM/yyyy';
  datePTAXExceeded: NdfDatePTAXExceeded | null = null;

  // Propriedades para gerenciar as parcelas
  installmentsOptions: InstallmentOption[] = [];
  selectedInstallments: string[] = [];
  installedInstallmentsCount: number = 0;
  private installmentsLoaded = false;

  constructor(
    private tourBuilder: TourBuilder,
    translateService: TranslateService,
    private datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private utilsService: UtilsService,
    store: Store<AppState>,
    private toast: ToastHelperService,
    private dialogService: ConfirmDialogService,
    private ndfCalculatorService: NdfCalculatorService,
    private cdr: ChangeDetectorRef,
    private loanOperationHttpService: LoanOperationHttpService,
    private dateFormatterService: DateFormatterService
  ) {
    super(translateService, store);
  }
  private getPtaxQuotationsSubject = new Subject<{ currencyId: string; cap: number }>();

  handleFormEdit() {
    if (!this.canEdit) {
      this.demandForm.disable();
      this.demandForm.get(this.demandRegisterProps.reference)?.enable();
    } else {
      this.demandForm.enable();
    }
  }

  private readonly initialTourSteps: TourStep[] = [
    {
      id: 'operationStrategyType',
      text: 'Agora é possível cadastrar operações de derivativos Call e Swap, além de NDF com CAP.',
      element: '.operationStrategyType',
      position: 'top',
      buttonText: 'Ok',
    },
  ];

  public initialTour: TourService = this.tourBuilder.build(this.initialTourSteps, {
    persistent: true,
    storageKey: 'derivatives-modalities-tour',
  });

  async ngOnInit() {
    console.log(
      `Inicializando componente para operação ${this.index}, loanId: ${this.demand?.loanOperationId}`
    );
    await this.loadInitialInstallments();
    /* setTimeout(() => {
      this.initialTour.start();
    }, 1000);*/
    if (this.store) {
      this.dateFormat = (await this.store
        .select(selectDateFormat)
        .pipe(first())
        .toPromise()) as string;
    }
    this.demandForm.get(this.demandRegisterProps.id)?.setValue(this.ndfOperation?.id);
    this.demandForm
      .get(this.demandRegisterProps.financialInstitution)
      ?.setValue(this.ndfOperation?.financialInstitutionId);
    this.demandForm
      .get(this.demandRegisterProps.operationDate)
      ?.setValue(this.ndfOperation?.operationDate || new Date().toISOString().split('T')[0]);
    this.demandForm.get(this.demandRegisterProps.dueAt)?.setValue(this.ndfOperation?.dueAt || '');
    this.demandForm
      .get(this.demandRegisterProps.daysCount)
      ?.setValue(this.ndfOperation?.daysCount || '');
    this.demandForm
      .get(this.demandRegisterProps.businessDaysCount)
      ?.setValue(this.ndfOperation?.businessDaysCount || '');
    this.demandForm
      .get(this.demandRegisterProps.notionalAmount)
      ?.setValue(this.ndfOperation?.notionalAmount || '');
    this.demandForm
      .get(this.demandRegisterProps.forwardRate)
      ?.setValue(this.ndfOperation?.forwardRate || '');
    this.demandForm
      .get(this.demandRegisterProps.capValue)
      ?.setValue(this.ndfOperation?.capValue || '');
    this.demandForm
      .get(this.demandRegisterProps.referenceExchangeRate)
      ?.setValue(this.ndfOperation?.referenceExchangeRate || '');
    this.demandForm
      .get(this.demandRegisterProps.reference)
      ?.setValue(this.ndfOperation?.reference || '');
    this.demandForm
      .get(this.demandRegisterProps.operationStrategyType)
      ?.setValue(this.ndfOperation?.operationStrategyType || 'NDFCAP');
    this.demandForm
      .get(this.demandRegisterProps.strikeBuy)
      ?.setValue(this.ndfOperation?.strikeBuy || '');
    this.demandForm
      .get(this.demandRegisterProps.strikeSell)
      ?.setValue(this.ndfOperation?.strikeSell || '');
    this.demandForm
      .get(this.demandRegisterProps.premium)
      ?.setValue(this.ndfOperation?.premium || '');
    this.demandForm
      .get(this.demandRegisterProps.numberOfInstallments)
      ?.setValue(this.ndfOperation?.numberOfInstallments || '');
    this.demandForm
      .get(this.demandRegisterProps.activeIndexer)
      ?.setValue(this.ndfOperation?.activeIndexer || '');
    this.demandForm
      .get(this.demandRegisterProps.passiveIndexer)
      ?.setValue(this.ndfOperation?.passiveIndexer || '');
    this.demandForm
      .get(this.demandRegisterProps.activeFixedRatePerYear)
      ?.setValue(this.ndfOperation?.activeFixedRatePerYear || '');
    this.demandForm
      .get(this.demandRegisterProps.passiveFixedRatePerYear)
      ?.setValue(this.ndfOperation?.passiveFixedRatePerYear || '');
    this.actionIcons = this.fillActionItems();

    this.demandForm
      .get(this.demandRegisterProps.dayCountConvention)
      ?.setValue(this.ndfOperation?.dayCountConvention || '360');

    this.demandForm
      .get(this.demandRegisterProps.interestFlow)
      ?.setValue(this.ndfOperation?.interestFlow || '');

    this.demandForm
      .get(this.demandRegisterProps.amortizationFlow)
      ?.setValue(this.ndfOperation?.amortizationFlow || '');

    this.demandForm
      .get(this.demandRegisterProps.numberOfAmortizations)
      ?.setValue(this.ndfOperation?.numberOfAmortizations || '');

    this.demandForm
      .get(this.demandRegisterProps.estimetedCdiForPeriod)
      ?.setValue(this.ndfOperation?.estimetedCdiForPeriod || '');

    this.demandForm.valueChanges.subscribe(() => {
      this.checkForChanges();
      /* setTimeout(() => {
        this.updateDemand();
      }, 1000);*/
      this.minDueDate = this.getMinDueDate();
    });

    this.handleFormEdit();

    this.dueDateSubject.pipe(debounceTime(500)).subscribe(dueDateData => {
      const datesToSend = {} as NdfOperationDates;

      Object.keys(dueDateData).forEach(key => {
        if (
          dueDateData[key as keyof NdfOperationDates] &&
          dueDateData[key as keyof NdfOperationDates] !== '0'
        ) {
          datesToSend[key as keyof NdfOperationDates] = dueDateData[
            key as keyof NdfOperationDates
          ] as string;
        }
      });
      //this.utilsService.setIsLoading(true);
      this.ndfCalculatorService.postCalculateDueDate(datesToSend).subscribe({
        next: response => {
          //this.utilsService.setIsLoading(false);
          if (response) {
            const { dueAt, businessDaysCount, daysCount, operationDate } = response;

            this.demandForm.patchValue({
              [this.demandRegisterProps.dueAt]: dueAt,
              [this.demandRegisterProps.businessDaysCount]: businessDaysCount,
              [this.demandRegisterProps.daysCount]: daysCount,
              [this.demandRegisterProps.operationDate]: operationDate,
            });

            const demandToSend = {
              ...(this.demand as NdfDemandsCalculationsFiltered),
              companyId: this.demand?.companyId || null,
              operationType: this.demand?.operationType || '',
              internationalCurrencyId: this.demand?.internationalCurrencyId || '',
              transactionValue: this.demand?.transactionValue || 0,
              operations: this.demand?.operations.map((item, index) => {
                if (index === this.index) {
                  return {
                    ...item,
                    dueAt,
                    businessDaysCount,
                    daysCount,
                    operationDate,
                  };
                }
                return item;
              }),
            } as NdfDemandsCalculationsFiltered;

            this.updateDemandAndNotify(demandToSend);
          }
        },
        error: error => {
          //this.utilsService.setIsLoading(false);
          console.error('handleCalculateDueDate error: ', error);

          this.toast.showToastError(
            this.t(this.tk.Management.ErrorCalculatingDueDate),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
    });

    this.getPtaxQuotationsSubject
      .pipe(
        debounceTime(800) // Tempo de debounce em milissegundos
      )
      .subscribe(({ currencyId, cap }) => {
        this.getPtaxQuotations(currencyId, cap);
      });

    this.loadAvailableInstallments();
  }
  private demandUpdateSubject = new Subject<{
    demand: NdfDemandsCalculationsFiltered | null;
    postSimulation: boolean;
  }>();
  private demandUpdateInitialized = false;

  private initializeDemandDebounce(): void {
    if (this.demandUpdateInitialized) return;
    this.demandUpdateInitialized = true;

    this.demandUpdateSubject
      .pipe(switchMap(data => of(data).pipe(delay(1000))))
      .subscribe(({ demand, postSimulation }) => {
        this.demand = demand;

        if (demand) {
          this.updateDemand(postSimulation);
        }
      });
  }

  updateDemandAndNotify(
    updatedDemand: NdfDemandsCalculationsFiltered | null,
    postSimulation: boolean = true
  ): void {
    //Comenta a linha abaixo para evitar o debounce e não estava enviando a demanda atualizada
    // this.initializeDemandDebounce();
    // this.demandUpdateSubject.next({ demand: updatedDemand, postSimulation });
    this.demand = updatedDemand;

    if (updatedDemand) {
      this.updateDemand(postSimulation);
    }
  }

  handleOperationStrategyChange(strategyType: string): void {
    // Objeto com os campos a serem zerados por estratégia
    const fieldsToReset = {
      NDFVanilla: [
        // Campos CAP
        this.demandRegisterProps.capValue,
        // Campos Call Spread/Call Vanilla
        this.demandRegisterProps.strikeBuy,
        this.demandRegisterProps.strikeSell,
        this.demandRegisterProps.premium,
        // Campos SWAP
        this.demandRegisterProps.numberOfInstallments,
        this.demandRegisterProps.numberOfAmortizations,
        this.demandRegisterProps.interestFlow,
        this.demandRegisterProps.amortizationFlow,
        this.demandRegisterProps.activeIndexer,
        this.demandRegisterProps.passiveIndexer,
        this.demandRegisterProps.activeFixedRatePerYear,
        this.demandRegisterProps.passiveFixedRatePerYear,
      ],
      NDFCAP: [
        // Campos Call Spread/Call Vanilla
        this.demandRegisterProps.strikeBuy,
        this.demandRegisterProps.strikeSell,
        this.demandRegisterProps.premium,
        // Campos SWAP
        this.demandRegisterProps.numberOfInstallments,
        this.demandRegisterProps.numberOfAmortizations,
        this.demandRegisterProps.interestFlow,
        this.demandRegisterProps.amortizationFlow,
        this.demandRegisterProps.activeIndexer,
        this.demandRegisterProps.passiveIndexer,
        this.demandRegisterProps.activeFixedRatePerYear,
        this.demandRegisterProps.passiveFixedRatePerYear,
      ],
      Callspread: [
        // Campos SWAP
        this.demandRegisterProps.numberOfInstallments,
        this.demandRegisterProps.numberOfAmortizations,
        this.demandRegisterProps.interestFlow,
        this.demandRegisterProps.amortizationFlow,
        this.demandRegisterProps.activeIndexer,
        this.demandRegisterProps.passiveIndexer,
        this.demandRegisterProps.activeFixedRatePerYear,
        this.demandRegisterProps.passiveFixedRatePerYear,
        this.demandRegisterProps.forwardRate,
      ],
      CallVanilla: [
        // Campos Call Spread
        this.demandRegisterProps.strikeSell,
        // Campos SWAP
        this.demandRegisterProps.numberOfInstallments,
        this.demandRegisterProps.numberOfAmortizations,
        this.demandRegisterProps.interestFlow,
        this.demandRegisterProps.amortizationFlow,
        this.demandRegisterProps.activeIndexer,
        this.demandRegisterProps.passiveIndexer,
        this.demandRegisterProps.activeFixedRatePerYear,
        this.demandRegisterProps.passiveFixedRatePerYear,
        this.demandRegisterProps.forwardRate,
      ],
      SWAP: [
        // Campos Call Spread/Call Vanilla
        this.demandRegisterProps.strikeBuy,
        this.demandRegisterProps.strikeSell,
        this.demandRegisterProps.premium,
        this.demandRegisterProps.forwardRate,
        this.demandRegisterProps.capValue,
      ],
    };

    // Pega os campos que devem ser zerados para a estratégia selecionada
    const fieldsToResetForStrategy =
      fieldsToReset[strategyType as keyof typeof fieldsToReset] || [];

    // Cria um objeto com os campos a serem zerados
    const resetValues = fieldsToResetForStrategy.reduce(
      (acc, field) => {
        acc[field] = '';
        return acc;
      },
      {} as Record<string, string>
    );

    // Atualiza apenas os campos que devem ser zerados
    this.demandForm.patchValue(resetValues);
  }
  getMinDueDate(): Date | null {
    return this.demandForm.get(this.demandRegisterProps.operationDate)?.value
      ? parseISO(this.demandForm.get(this.demandRegisterProps.operationDate)?.value)
      : null;
  }

  getIndexerLabel(indexerValue: string | undefined): string {
    if (!indexerValue) return '-';

    const option = this.activeIndexerOptions.find(opt => opt.value === indexerValue);
    return option ? option.label : indexerValue;
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['demand'] || simpleChanges['ndfOperation']) {
      const changedDemand = simpleChanges['demand']?.currentValue as NdfDemandsCalculationsFiltered;

      // Always refresh the action menu when component changes
      this.actionIcons = this.fillActionItems();

      if (changedDemand) {
        if (
          this.ndfOperation &&
          this.ndfOperation.capValue &&
          this.datePTAXExceeded?.cap !== this.ndfOperation.capValue
        ) {
          this.getPtaxQuotationsSubject.next({
            currencyId: this.demand?.internationalCurrencyId as string,
            cap: this.ndfOperation.capValue,
          });
        }
        this.loadAvailableInstallments();
      }

      this.handleFormEdit();
    }
  }

  handleFormControlAction(action: 'highlight' | 'block', controlName: string) {
    if (action && controlName && (this.demand?.operations || []).length > 1) {
      this.sendOperationAction.emit({
        action,
        item: this.ndfOperation as NdfDemandsCalculationsOperation,
        index: this.index,
        controlName,
      });
    }
  }

  getLastDayPTAXExceededCapFromToDay(quoteDate: string): number {
    if (!quoteDate) {
      return 0;
    }

    const toDay = new Date();
    const quoteDateFormatted = new Date(quoteDate);

    const diffInMilliseconds = toDay.getTime() - quoteDateFormatted.getTime();
    const daysDifference = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

    return daysDifference;
  }

  moveOrder(direction: 'move-up' | 'move-down' | 'move-last'): void {
    if (!this.ndfOperation) return;
    this.sendOperationAction.emit({
      action: direction,
      item: this.ndfOperation,
      index: this.index,
    });
  }

  getPtaxQuotations(currencyId: string, cap: number): void {
    //this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getPtaxQuotationsHighestAboveCap(currencyId, cap).subscribe({
      next: response => {
        // Remove loading indicator here
        // this.utilsService.setIsLoading(false);
        if (response) {
          const { quoteDate } = response;
          this.datePTAXExceeded = {
            cap,
            lastDatePTAXExceededCap: quoteDate as unknown as string,
            daysUntilLastPTAXExceedCap: this.getLastDayPTAXExceededCapFromToDay(
              quoteDate as unknown as string
            ),
          };
        } else {
          this.datePTAXExceeded = {
            cap,
            lastDatePTAXExceededCap: null,
            daysUntilLastPTAXExceedCap: null,
          };
        }
        this.cdr.detectChanges();
      },
      error: error => {
        // Remove loading indicator here
        // this.utilsService.setIsLoading(false);
        console.error('getPtaxQuotations error: ', error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingHighestTwelveMonthAboveCap),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  checkForChanges(): void {
    const formValue = { ...this.demandForm.getRawValue() };
    const formToSend = {
      capValue: formValue['capValue'],
      forwardRate: formValue['forwardRate'],
      id: formValue['id'],
      notionalAmount: formValue['notionalAmount'],
      referenceExchangeRate: formValue['referenceExchangeRate'],
      reference: formValue['reference'],
      financialInstitutionId: formValue['financialInstitution'],
      operationStrategyType: formValue['operationStrategyType'],
      strikeBuy: formValue['strikeBuy'],
      strikeSell: formValue['strikeSell'],
      premium: formValue['premium'],
      numberOfInstallments: formValue['numberOfInstallments'],
      activeIndexer: formValue['activeIndexer'],
      passiveIndexer: formValue['passiveIndexer'],
      activeFixedRatePerYear: formValue['activeFixedRatePerYear'],
      passiveFixedRatePerYear: formValue['passiveFixedRatePerYear'],
    };

    const operation = {
      ...this.ndfOperation,
      reference: this.ndfOperation?.reference || '',
    } as NdfDemandsCalculationsOperation;

    this.showApplyButton = !this.areValuesEqual(formToSend, operation);
  }

  canMoveUp(): boolean {
    return (
      this.ndfOperation?.order !== 1 &&
      this.demand?.operations?.filter(operation => operation.id === null).length === 0
    );
  }

  canMoveDown(): boolean {
    if (!this.ndfOperation || !this.demand?.operations) {
      return false;
    }

    const totalOperations = this.demand.operations.length ?? 0;
    const currentOrder = this.ndfOperation.order;
    const hasNullIds =
      this.demand.operations.filter(operation => operation.id === null).length === 0;

    return totalOperations !== currentOrder && hasNullIds;
  }

  areValuesEqual(
    formValue: Partial<{ [x: string]: string | null }>,
    operation: NdfDemandsCalculationsOperation | null
  ): boolean {
    if (!operation) {
      return false;
    }

    for (const key in formValue) {
      if (
        Object.prototype.hasOwnProperty.call(formValue, key) &&
        Object.prototype.hasOwnProperty.call(operation, key)
      ) {
        if (formValue[key] != operation[key as keyof NdfDemandsCalculationsOperation]) {
          return false;
        }
      }
    }
    return true;
  }

  get isPremiumFieldVisible(): boolean {
    // Mostrar apenas para Call Spread e Call Vanilla
    return (
      this.ndfOperation?.operationStrategyType === 'Callspread' ||
      this.ndfOperation?.operationStrategyType === 'CallVanilla'
    );
  }

  handleOptionChange(selectedOptions: Option | null, key: string): void {
    console.log('handleOptionChange', key);
    let demandToSend: NdfDemandsCalculationsFiltered | null = null;

    switch (key) {
      case 'operationStrategyType':
        if (!selectedOptions?.value) {
          return;
        }
        this.demandForm.patchValue({
          [this.demandRegisterProps.operationStrategyType]: selectedOptions?.value,
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              // Atualiza a operação, considerando a limpeza de campos
              const updatedOperation = {
                ...item,
                operationStrategyType: selectedOptions?.value,
              };

              // Se for NDFVanilla, também zerar o CAP
              if (selectedOptions?.value === 'NDFVanilla') {
                updatedOperation.capValue = 0;

                // Também atualize o form para refletir a mudança
                this.demandForm.patchValue({
                  [this.demandRegisterProps.capValue]: '0',
                });
              }

              // Se não for NDFCAP, zera o CAP
              if (selectedOptions?.value !== 'NDFCAP') {
                updatedOperation.capValue = 0;

                // Também atualize o form para refletir a mudança
                this.demandForm.patchValue({
                  [this.demandRegisterProps.capValue]: '0',
                });
              }

              // Limpa campos específicos conforme a modalidade
              if (
                selectedOptions?.value !== 'Callspread' &&
                selectedOptions?.value !== 'CallVanilla'
              ) {
                updatedOperation.strikeBuy = undefined;
                updatedOperation.strikeSell = undefined;
                updatedOperation.premium = undefined;
              }

              if (selectedOptions?.value !== 'SWAP') {
                updatedOperation.numberOfInstallments = undefined;
                updatedOperation.numberOfAmortizations = undefined;
                updatedOperation.interestFlow = undefined;
                updatedOperation.amortizationFlow = undefined;
                updatedOperation.activeIndexer = undefined;
                updatedOperation.passiveIndexer = undefined;
                updatedOperation.activeFixedRatePerYear = undefined;
                updatedOperation.passiveFixedRatePerYear = undefined;
              }

              if (
                selectedOptions?.value !== 'NDFCAP' &&
                selectedOptions?.value !== 'NDFVanilla' &&
                selectedOptions?.value !== 'CallVanilla' &&
                selectedOptions?.value !== 'Callspread'
              ) {
                updatedOperation.forwardRate = 0;
              }

              this.handleOperationStrategyChange(selectedOptions.value);
              return updatedOperation;
            }
            return item;
          }),
        };

        this.updateDemandAndNotify(demandToSend);

        this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.markAsTouched();
        break;
      case 'activeIndexer':
        if (!selectedOptions?.value) {
          return;
        }
        this.demandForm.patchValue({
          [this.demandRegisterProps.activeIndexer]: selectedOptions?.value,
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                activeIndexer: selectedOptions?.value,
              };
            }
            return item;
          }),
        };
        this.updateDemandAndNotify(demandToSend);

        this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.markAsTouched();
        break;

      case 'dayCountConvention':
        if (!selectedOptions?.value) {
          return;
        }
        this.demandForm.patchValue({
          [this.demandRegisterProps.dayCountConvention]: selectedOptions?.value,
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                dayCountConvention: Number(selectedOptions?.value),
              };
            }
            return item;
          }),
        };
        this.updateDemandAndNotify(demandToSend);

        this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.markAsTouched();
        break;
      case 'passiveIndexer':
        if (!selectedOptions?.value) {
          return;
        }
        this.demandForm.patchValue({
          [this.demandRegisterProps.passiveIndexer]: selectedOptions?.value,
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                passiveIndexer: selectedOptions?.value,
              };
            }
            return item;
          }),
        };
        this.updateDemandAndNotify(demandToSend);

        this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.markAsTouched();
        break;
      case 'financialInstitution':
        if (
          !selectedOptions?.value ||
          selectedOptions.value == this.selectedFinancialInstitution?.value
        ) {
          return;
        }
        this.demandForm.patchValue({
          [this.demandRegisterProps.financialInstitution]: selectedOptions?.value,
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: this.demand?.operations.map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                financialInstitutionId: selectedOptions?.value,
              };
            }
            return item;
          }),
        } as NdfDemandsCalculationsFiltered;

        this.updateDemandAndNotify(demandToSend);

        this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
        this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.markAsTouched();

        break;
    }

    if (demandToSend) {
      this.updateDemandAndNotify(demandToSend);
    }
  }
  handleCalendarValue(dates: string[], key: string): void {
    const dueAt = this.demandForm.get(this.demandRegisterProps.dueAt);
    const operationDate = this.demandForm.get(this.demandRegisterProps.operationDate);

    const innerDates = dates.join(',').split('T')[0];

    const demandToSend = {
      ...this.demand,
    } as NdfDemandsCalculationsFiltered;

    switch (key) {
      case 'operationDate':
        this.demandForm.patchValue({
          [this.demandRegisterProps.operationDate]: innerDates,
        });

        if (dueAt?.value) {
          if (parseISO(dueAt?.value) < parseISO(innerDates)) {
            this.demandForm.patchValue({
              [this.demandRegisterProps.dueAt]: '',
            });
            this.demandForm.patchValue({
              [this.demandRegisterProps.daysCount]: '',
            });
            this.demandForm.patchValue({
              [this.demandRegisterProps.businessDaysCount]: '',
            });
          }
        }
        demandToSend.operations = demandToSend.operations.map((item, index) => {
          if (index === this.index) {
            return {
              ...item,
              operationDate: innerDates,
            };
          }
          return item;
        });

        this.updateDemandAndNotify(demandToSend);

        if (dueAt?.value && operationDate?.value) {
          this.handleCalculateDueDate();
        }
        break;
      case 'dueAt':
        this.demandForm.patchValue({
          [this.demandRegisterProps.dueAt]: innerDates,
        });
        demandToSend.operations = demandToSend.operations.map((item, index) => {
          if (index === this.index) {
            return {
              ...item,
              dueAt: innerDates,
            };
          }
          return item;
        });
        this.updateDemandAndNotify(demandToSend);

        if (dueAt?.value && operationDate?.value) {
          this.handleCalculateDueDate();
        } else {
          this.demandForm.get(this.demandRegisterProps.dueAt)?.markAsTouched();
          this.demandForm.get(this.demandRegisterProps.financialInstitution)?.markAsTouched();
        }
        break;
    }
  }

  handleInputText(inputData: IdValue, key: string) {
    const demandToSend = {
      ...this.demand,
    } as NdfDemandsCalculationsFiltered;
    switch (key) {
      case 'reference':
        this.demandForm.patchValue({
          [this.demandRegisterProps.reference]: inputData?.value as string,
        });
        demandToSend.operations = demandToSend.operations.map((item, index) => {
          if (index === this.index) {
            return {
              ...item,
              reference: inputData?.value as string,
            };
          }
          return item;
        });

        this.updateDemandAndNotify(demandToSend, false);

        setTimeout(() => inputData.target?.focus(), 1);
        break;
    }
  }

  handleInputNumber(inputData: IdValue, key: string): void {
    console.log('handleInputNumber', inputData, key);
    let demandToSend = {
      ...this.demand,
    } as NdfDemandsCalculationsFiltered;

    switch (key) {
      case 'strikeBuy':
      case 'strikeSell':
      case 'premium':
      case 'numberOfInstallments':
      case 'activeFixedRatePerYear':
      case 'passiveFixedRatePerYear':
        this.demandForm.patchValue({
          [this.demandRegisterProps[key]]: String(inputData.value),
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                [key]: String(inputData.value),
              };
            }
            return item;
          }),
        };
        break;
      case 'notionalAmount':
      case 'forwardRate':
      case 'capValue':
      case 'referenceExchangeRate':
      case 'daysCount':
      case 'businessDaysCount':
        this.demandForm.patchValue({
          [this.demandRegisterProps[key]]: String(inputData.value),
        });
        demandToSend = {
          ...(this.demand as NdfDemandsCalculationsFiltered),
          operations: (this.demand?.operations || []).map((item, index) => {
            if (index === this.index) {
              return {
                ...item,
                [key]: String(inputData.value),
              };
            }
            return item;
          }),
        };
        break;
    }

    if (demandToSend) {
      this.updateDemandAndNotify(demandToSend);
    }
  }

  handleCalculateDueDate(): void {
    const { daysCount, businessDaysCount, dueAt, operationDate } = this.demandForm.value;
    if (!dueAt && !operationDate) return;

    const dueDateData: NdfOperationDates = {
      daysCount,
      businessDaysCount,
      dueAt,
      operationDate,
    } as NdfOperationDates;

    this.dueDateSubject.next(dueDateData);
  }

  fillActionItems(): MenuItem[] {
    let status = [
      {
        label: this.ndfOperation?.colapsed
          ? this.t(this.tk.General.Expand1)
          : this.t(this.tk.General.Shrink),
        icon: this.ndfOperation?.colapsed ? 'pi pi-eye' : 'pi pi-eye-slash',
        action: 'pin-unpin',
        disabled: false,
        order: 0,
      },
      {
        label: 'Anexos',
        icon: 'pi pi-paperclip',
        action: 'attachments',
        disabled: !this.ndfOperation?.id,
        order: 1,
      },
      {
        label: this.ndfOperation?.selected
          ? this.t(this.tk.General.Uncheck)
          : this.t(this.tk.General.Select1),
        icon: this.ndfOperation?.selected ? 'pi pi-check-circle' : 'pi pi-circle-off ',
        action: 'check-uncheck',
        disabled: false,
        order: 0,
      },
      {
        label: this.t(this.tk.General.Duplicate),
        icon: 'pi pi-clone',
        action: 'duplicate',
        disabled: false,
        order: 3,
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'remove',
        disabled: this.canDelete(),
        order: 4,
      },
    ];

    if (this.ndfOperation?.operationStatus === 'Simulated') {
      status.push({
        label: this.t(this.tk.Management.NdfQuote),
        icon: 'pi pi-building-columns',
        action: 'quote',
        disabled: false,
        order: 1,
      });
    }

    if (this.ndfOperation?.operationStatus === 'Quoted') {
      status.push({
        label: 'Reverter cotação',
        icon: 'pi pi-undo',
        action: 'revert-quoted-to-simulated',
        disabled: false,
        order: 1,
      });
    }

    if (
      this.ndfOperation?.operationStatus === 'Hired' &&
      this.ndfOperation?.financialStatus === 'Open'
    ) {
      status.push({
        label: 'Reverter contratação',
        icon: 'pi pi-undo',
        action: 'revert-hired-to-quoted',
        disabled: false,
        order: 1,
      });
    }

    if (
      this.ndfOperation?.operationStatus === 'Hired' &&
      this.ndfOperation?.financialStatus === 'Settled'
    ) {
      status.push({
        label: 'Reverter liquidação',
        icon: 'pi pi-undo',
        action: 'revert-liquidation-to-open',
        disabled: false,
        order: 1,
      });
    }

    if (
      this.ndfOperation?.operationStatus === 'Quoted' &&
      this.ndfOperation?.financialStatus !== 'Reversed'
    ) {
      status.push({
        label: this.t(this.tk.Management.NdfToHire),
        icon: 'pi pi-bookmark',
        action: 'hire',
        disabled: false,
        order: 2,
      });
    }

    status = status.sort((a, b) => a.order - b.order);

    return status;
  }
  handleDoubleClickHeader() {
    this.handleActionMenu({ action: 'pin-unpin' });
  }
  handleLongPress() {
    this.handleActionMenu({ action: 'check-uncheck' });
  }

  handleActionMenu(menuItem: MenuItem): void {
    console.log('handleActionMenu', menuItem);
    const { action } = menuItem;

    let demandToSend = {
      ...this.demand,
    } as NdfDemandsCalculationsFiltered;

    let { operations } = demandToSend;

    const selectedOperation = demandToSend.operations.find((item, index) => index === this.index);
    switch (action) {
      case 'duplicate':
        if (selectedOperation) {
          // Create a deep copy to avoid read-only property issues
          const newOperation = JSON.parse(
            JSON.stringify({
              ...selectedOperation,
              id: null,
              sequentialId: undefined,
              operationStatus: undefined,
              financialStatus: undefined,
              composition: undefined,
              order: operations.length + 1, // Define a order para o último item
              // Add an empty installmentIds array if it doesn't exist
              installmentIds: [],
            })
          );

          operations = [...operations, newOperation];
          demandToSend = {
            ...demandToSend,
            operations,
          } as NdfDemandsCalculationsFiltered;
          this.updateDemandAndNotify(demandToSend);

          this.toast.showToastSuccess(this.t(this.tk.Management.NdfDuplicateSimulation));
        }
        break;
      case 'remove': {
        const operation = demandToSend.operations[this.index];

        if (!this.canEdit) {
          this.toast.showToastWarn(this.t(this.tk.Management.OperationsRemovalSecurity));
          return;
        }

        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        demandToSend.operations = demandToSend.operations.map((operation, newIndex) => ({
          ...operation,
          order: newIndex + 1, // Assumindo que a ordem começa em 1
        }));

        if (operation?.id) {
          this.sendOperationAction.emit({
            action: 'remove',
            item: selectedOperation as NdfDemandsCalculationsOperation,
            index: this.index,
          });
        } else {
          this.updateDemandAndNotify(demandToSend);
        }
        break;
      }
      case 'quote':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        this.sendOperationAction.emit({
          action: 'quote',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
      case 'hire':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        this.sendOperationAction.emit({
          action: 'hire',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
      case 'pin-unpin':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );

        if (demandToSend?.id) {
          this.sendOperationAction.emit({
            action: 'pin-unpin',
            item: {
              ...selectedOperation,
              collapsed: selectedOperation?.colapsed,
            } as NdfDemandsCalculationsOperation,
            index: this.index,
          });

          //this.moveOrder('move-last');
        }
        break;
      case 'check-uncheck':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );

        if (demandToSend?.id) {
          this.sendOperationAction.emit({
            action: 'check-uncheck',
            item: {
              ...selectedOperation,
              selected: selectedOperation?.selected,
            } as NdfDemandsCalculationsOperation,
            index: this.index,
          });
        }
        break;
      case 'revert-quoted-to-simulated':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        this.sendOperationAction.emit({
          action: 'revert-quoted-to-simulated',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
      case 'revert-liquidation-to-open':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        this.sendOperationAction.emit({
          action: 'revert-liquidation-to-open',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
      case 'revert-hired-to-quoted':
        demandToSend.operations = demandToSend.operations.filter(
          (item, index) => index !== this.index
        );
        this.sendOperationAction.emit({
          action: 'revert-hired-to-quoted',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
      case 'attachments':
        this.sendOperationAction.emit({
          action: 'attachments',
          item: selectedOperation as NdfDemandsCalculationsOperation,
          index: this.index,
        });
        break;
    }
  }

  canDelete(): boolean {
    return this.index === 0 && this.demand?.operations.length === 1;
  }

  get selectedFinancialInstitution(): Option | null {
    const value =
      this.financialInstitutionsOptions.find(
        item => item.value === this.ndfOperation?.financialInstitutionId
      ) || null;
    return value;
  }

  get daysCount(): string {
    return (this.ndfOperation?.daysCount || '').toString() || '';
  }

  get businessDaysCount(): string {
    return (this.ndfOperation?.businessDaysCount || '').toString() || '';
  }

  get notionalAmount(): string {
    return (this.ndfOperation?.notionalAmount || '').toString() || '';
  }

  get forwardRate(): string {
    return (this.ndfOperation?.forwardRate || '').toString() || '';
  }

  get capValue(): string {
    return (this.ndfOperation?.capValue || '').toString() || '';
  }

  get statusLabel(): string {
    switch (this.ndfOperation?.operationStatus) {
      case 'Simulated':
        return this.t(this.tk.Management.NdfSimulated);
      case 'Quoted':
        return this.t(this.tk.Management.NdfQuoted);
      case 'Hired':
        return this.t(this.tk.Management.NdfHired);
      default:
        return '';
    }
  }

  get referenceExchangeRate(): string {
    return (this.ndfOperation?.referenceExchangeRate || '').toString() || '';
  }
  getTest() {
    return this.demand?.operations?.map(x => x.notionalAmount);
  }

  updateDemand(postSimulation: boolean = true) {
    // Always ensure installments are synchronized before updating
    this.ensureInstallmentsSelected();

    this.showApplyButton = false;
    this.sendDemand.emit({ demand: this.demand, postSimulation });
  }

  convertDate(dateString: string): string {
    if (!dateString) {
      return dateString;
    }
    const date = new Date(dateString);
    const day = this.datePipe.transform(date, 'dd');
    const month = this.datePipe.transform(date, 'MMM');
    const year = this.datePipe.transform(date, 'yyyy');
    return `${day} de ${month} - ${year}`;
  }

  get operationStatusLabel(): string {
    switch (this.ndfOperation?.operationStatus) {
      case 'Simulated':
        return this.t(this.tk.Management.NdfSimulated);
      case 'Quoted':
        return this.t(this.tk.Management.NdfQuoted);
      case 'Hired':
        return this.t(this.tk.Management.NdfHired);
      default:
        return '';
    }
  }

  get operationStatusBlockEdition(): boolean {
    return (
      this.ndfOperation?.operationStatus === 'Quoted' ||
      this.ndfOperation?.operationStatus === 'Hired'
    );
  }

  get financialStatusLabel(): string {
    switch (this.ndfOperation?.financialStatus) {
      case 'Open':
        return this.t(this.tk.Management.NdfOpen);
      case 'Settled':
        return this.t(this.tk.Management.NdfSettled);
      case 'Reversed':
        return this.t(this.tk.Management.NdfSettledReversed);
      default:
        return '';
    }
  }

  get compositionLabel(): string {
    switch (this.ndfOperation?.composition) {
      case 'Full':
        return this.t(this.tk.Management.NdfFull);
      case 'Partial':
        return this.t(this.tk.Management.NdfPartial);
      default:
        return '';
    }
  }

  get selectedOperationStrategyType(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.operationStrategyType)?.value;
    return value
      ? (this.operationStrategyTypeOptions.find(opt => opt.value === value) ?? null)
      : null;
  }

  get selectedActiveIndexer(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.activeIndexer)?.value;
    return value ? (this.activeIndexerOptions.find(opt => opt.value === value) ?? null) : null;
  }

  get selectedPassiveIndexer(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.passiveIndexer)?.value;
    return value ? (this.passiveIndexerOptions.find(opt => opt.value === value) ?? null) : null;
  }

  get selectedDayCountConvention(): Option | null {
    const value = this.demandForm.get(this.demandRegisterProps.dayCountConvention)?.value || '360';
    return value ? (this.dayCountConventionOptions.find(opt => opt.value === value) ?? null) : null;
  }

  override ngOnDestroy(): void {
    this.dueDateSubject.unsubscribe();
  }
  get forwardRateValue(): string {
    const value = this.demandForm.get(this.demandRegisterProps.forwardRate)?.value;
    return value !== null && value !== undefined ? value.toString() : '';
  }

  get isSwapAndCDI(): boolean {
    return (
      this.selectedOperationStrategyType?.value === 'SWAP' &&
      this.selectedPassiveIndexer?.value === 'CDI'
    );
  }

  get isDueDateInFuture(): boolean {
    if (!this.ndfOperation?.dueAt) return false;
    const dueDate = this.dateFormatterService.parseDate(this.ndfOperation.dueAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part to compare dates only

    if (!dueDate) return false;
    return dueDate > today;
  }

  get shouldShowAlertIcons(): boolean {
    return (this.isSwapAndCDI && this.isDueDateInFuture) || this.isCallSpread;
  }

  getDayCountConventionLabel(value: string | undefined): string {
    if (!value) return ' - ';

    const valueAsNumber = Number(value);
    const option = this.dayCountConventionOptions.find(opt => Number(opt.value) === valueAsNumber);

    return option?.label || ' - ';
  }

  loadAvailableInstallments(): void {
    console.log(
      `loadAvailableInstallments chamado para operação ${this.index}, já tem parcelas? ${this.ndfOperation?.derivativeOperationInstallments?.length}`
    );

    // Se já existem parcelas do ndfOperation, use-as diretamente
    if (this.ndfOperation?.derivativeOperationInstallments?.length) {
      // Se já temos as opções de instalação carregadas, não precisamos construí-las novamente
      if (this.installmentsOptions.length === 0 || this.installmentsOptions[0].value === '') {
        this.installmentsOptions = this.ndfOperation.derivativeOperationInstallments.map(
          installment => {
            // Verifica se é Flat Fee baseado na propriedade isFlatFee da parcela
            const isFlatFee = installment.loanOperationInstallment?.isFlatFee || false;

            const label = isFlatFee
              ? `Flat Fee | ${this.t(this.tk.Management.Expiration1)}: ${this.datePipe.transform(installment.loanOperationInstallment?.dueAt, 'dd/MM/yyyy')}`
              : `${installment.loanOperationInstallment?.installmentNumber} | ${this.t(this.tk.Management.Expiration1)}: ${this.datePipe.transform(installment.loanOperationInstallment?.dueAt, 'dd/MM/yyyy')}`;

            return {
              label: label,
              value: installment.loanOperationInstallmentId,
              isFlatFee: isFlatFee,
            };
          }
        );
      }

      // Seleciona as parcelas já vinculadas à operação
      this.selectedInstallments = this.ndfOperation.derivativeOperationInstallments.map(
        x => x.loanOperationInstallmentId
      );

      this.installedInstallmentsCount = this.selectedInstallments.length;
    }
    // Se houver um empréstimo vinculado e já estiver no cache, use o cache
    else if (
      this.demand?.loanOperationId &&
      NdfManagementItemComponent.loanOperationInstallmentsCache[this.demand.loanOperationId]
    ) {
      console.log('Usando cache para: ' + this.demand.loanOperationId);
      // Usa as parcelas do cache
      const loanOperation =
        NdfManagementItemComponent.loanOperationInstallmentsCache[this.demand.loanOperationId];

      if (loanOperation?.flowParams?.installments?.length) {
        this.installmentsOptions = loanOperation.flowParams.installments.map(
          (installment: LoanInstallment) => {
            const isFlatFee = installment.isFlatFee || false;

            const label = isFlatFee
              ? `Flat Fee | Vencimento: ${this.datePipe.transform(installment.dueAt, 'dd/MM/yyyy')}`
              : `${installment.installmentNumber} | ${this.t(this.tk.Management.Expiration1)}: ${this.datePipe.transform(installment.dueAt, 'dd/MM/yyyy')}`;

            return {
              label: label,
              value: installment.id,
              isFlatFee: isFlatFee,
            };
          }
        );

        // Marca que as parcelas foram carregadas
        this.installmentsLoaded = true;
        NdfManagementItemComponent.loadedLoanIds.add(this.demand.loanOperationId);
      }
    }
    // Se houver um empréstimo vinculado, mas não estiver no cache
    else if (this.demand?.loanOperationId && !this.installmentsLoaded) {
      // Chama o método de carregamento inicial apenas se não foi carregado
      this.loadInitialInstallments();
    }
    // Se não houver empréstimo vinculado ou parcelas disponíveis
    else if (!this.installmentsLoaded) {
      this.installmentsOptions = [{ label: 'Nenhuma parcela disponível', value: '' }];
      this.selectedInstallments = [];
      this.installedInstallmentsCount = 0;
    }

    // Forçar detecção de mudanças para atualizar a UI
    this.cdr.detectChanges();
  }

  // Método para lidar com mudanças na seleção de parcelas
  handleInstallmentsChange(event: { value: string[] }): void {
    this.selectedInstallments = event.value || [];
    this.installedInstallmentsCount = this.selectedInstallments.length;

    // Atualiza a demanda com as parcelas selecionadas
    if (this.demand) {
      const demandToSend = {
        ...(this.demand as NdfDemandsCalculationsFiltered),
        operations: (this.demand?.operations || []).map((item, index) => {
          if (index === this.index) {
            return {
              ...item,
              installmentIds: this.selectedInstallments,
            };
          }
          return item;
        }),
      };

      this.updateDemandAndNotify(demandToSend);
    }
  }

  // Adicione este atributo estático para controlar quais loan IDs já foram carregados
  private static loadedLoanIds: Set<string> = new Set<string>();
  private static loanOperationInstallmentsCache: Record<string, LoanOperation> = {};

  private async loadInitialInstallments(): Promise<void> {
    console.log(
      `Tentando carregar parcelas para operação ${this.index}, loanId: ${this.demand?.loanOperationId}, já carregado? ${this.installmentsLoaded}`
    );
    // Verifica se há um empréstimo vinculado
    if (!this.demand?.loanOperationId) {
      // Se não houver empréstimo vinculado
      this.installmentsOptions = [{ label: 'Nenhuma parcela disponível', value: '' }];
      this.selectedInstallments = [];
      this.installedInstallmentsCount = 0;
      this.cdr.detectChanges();
      return;
    }

    // Verificação mais forte: se já foi carregado para esta instância OU para este ID empréstimo, não carregue novamente
    if (
      this.installmentsLoaded ||
      NdfManagementItemComponent.loadedLoanIds.has(this.demand.loanOperationId)
    ) {
      // Se já tiver carregado as instalações, apenas configure as selecionadas
      if (this.ndfOperation?.derivativeOperationInstallments?.length) {
        this.selectedInstallments = this.ndfOperation.derivativeOperationInstallments.map(
          x => x.loanOperationInstallmentId
        );
        this.installedInstallmentsCount = this.selectedInstallments.length;
      }
      return;
    }

    try {
      let loanOperation: LoanOperation;

      // Adicione este ID ao conjunto de IDs já carregados
      NdfManagementItemComponent.loadedLoanIds.add(this.demand.loanOperationId);

      // Verifica se já existe no cache global
      if (NdfManagementItemComponent.loanOperationInstallmentsCache[this.demand.loanOperationId]) {
        loanOperation =
          NdfManagementItemComponent.loanOperationInstallmentsCache[this.demand.loanOperationId];
        console.log('Usando cache para loan ID:', this.demand.loanOperationId);
      } else {
        // Carrega as parcelas do empréstimo apenas se não estiver no cache
        console.log('Carregando novo loan ID:', this.demand.loanOperationId);
        loanOperation = (await this.loanOperationHttpService
          .find(this.demand.loanOperationId)
          .toPromise()) as LoanOperation;

        // Armazena no cache
        if (loanOperation) {
          NdfManagementItemComponent.loanOperationInstallmentsCache[this.demand.loanOperationId] =
            loanOperation;
        }
      }

      if (loanOperation?.flowParams?.installments?.length) {
        // Mapeia as parcelas para o formato do multiselect
        this.installmentsOptions = loanOperation.flowParams.installments.map(
          (installment: LoanInstallment) => {
            const isFlatFee = installment.isFlatFee || false;

            const label = isFlatFee
              ? `Flat Fee | Vencimento: ${this.datePipe.transform(installment.dueAt, 'dd/MM/yyyy')}`
              : `${installment.installmentNumber} | ${this.t(this.tk.Management.Expiration1)}: ${this.datePipe.transform(installment.dueAt, 'dd/MM/yyyy')}`;

            return {
              label: label,
              value: installment.id,
              isFlatFee: isFlatFee,
            };
          }
        );

        // Verifica se já existem parcelas vinculadas na operação
        if (this.ndfOperation?.derivativeOperationInstallments?.length) {
          // Seleciona os IDs das parcelas vinculadas
          this.selectedInstallments = this.ndfOperation.derivativeOperationInstallments.map(
            x => x.loanOperationInstallmentId
          );
          this.installedInstallmentsCount = this.selectedInstallments.length;
        }
      }

      // Marca que as parcelas foram carregadas para não executar novamente
      this.installmentsLoaded = true;

      // Força detecção de mudanças
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Erro ao carregar parcelas iniciais:', error);

      // Tratamento de erro
      this.installmentsOptions = [{ label: 'Erro ao carregar parcelas', value: '' }];
      this.cdr.detectChanges();
    }
  }

  ensureInstallmentsSelected(): void {
    // If there are already installments but selectedInstallments is empty, sync them
    if (
      this.ndfOperation?.derivativeOperationInstallments?.length &&
      this.selectedInstallments.length === 0
    ) {
      this.selectedInstallments = this.ndfOperation.derivativeOperationInstallments.map(
        x => x.loanOperationInstallmentId
      );
    }

    // Make sure to sync the installmentIds on the operation with the current selectedInstallments
    if (this.demand && this.index !== undefined) {
      const updatedDemand = JSON.parse(JSON.stringify(this.demand)); // Create a deep copy to avoid read-only issues
      if (updatedDemand.operations[this.index]) {
        updatedDemand.operations[this.index].installmentIds = [...this.selectedInstallments];
      }
      this.demand = updatedDemand;
    }
  }

  // Function to generate the correct label based on selected count
  getSelectedItemsLabel(): string {
    return this.selectedInstallments.length === 1
      ? '1 ' + this.t(this.tk.Management.LinkedInstallment)
      : `${this.selectedInstallments.length} ${this.t(this.tk.Management.LinkedInstallments)}`;
  }
}
